import React from 'react';

import { CircularProgress } from '@/components/custom-ui/circular-progress';
import { Image } from '@/components/custom-ui/Image';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useAppTheme } from '@/theme/themeProvider';

import type {
  ICompanyItemGroupedInterface,
  ICompanyItemInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

import { FormDialog } from './FormDialog';

type Props = {
  allCompanies: ICompanyList[] | undefined;
  setAllData: React.Dispatch<
    React.SetStateAction<ICompanyItemInterface[] | null>
  >;
  setFilterData: React.Dispatch<
    React.SetStateAction<ICompanyItemInterface[] | null>
  >;
  setGroupedData: React.Dispatch<
    React.SetStateAction<ICompanyItemGroupedInterface[] | null>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedCompany: React.Dispatch<React.SetStateAction<ICompanyList | null>>;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
  loading: boolean;
};

function ReportHeader({
  allCompanies,
  loading,
  setAllData,
  setFilterData,
  setGroupedData,
  setLoading,
  setEndDate,
  setSelectedCompany,
  setStartDate,
}: Props) {
  const { isDark } = useAppTheme();

  return (
    <Card className="mb-4 border-2 border-indigo-200/70 shadow-sm dark:border-indigo-800/40">
      <CardContent className="p-4">
        <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          {/* Left: Icon + Title */}
          <div className="flex flex-1 items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-md dark:from-blue-500 dark:to-indigo-500">
              <Image
                alt="Company Report"
                src={isDark ? '/images/svg/note.svg' : '/images/svg/note.svg'}
                isBlurred
                className="h-7 w-7 brightness-0 invert filter"
              />
            </div>
            <div className="space-y-0.5 text-right">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                تقرير حركة اصناف الشركات تفصيلي
              </h1>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                تفصيلي اصناف الشركات بيع ومرتجع و وارد
              </p>
            </div>
          </div>

          {/* Right: Action or Loading */}
          <div className="flex-shrink-0">
            {allCompanies ? (
              <FormDialog
                setAllData={setAllData}
                setGroupedData={setGroupedData}
                setFilterData={setFilterData}
                setLoading={setLoading}
                companies={allCompanies}
                loading={loading}
                setSelectedCompany={setSelectedCompany}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            ) : (
              <Badge
                variant="outline"
                className="flex h-11 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-indigo-200 bg-white/70 px-4 py-2 text-sm font-semibold text-indigo-700 dark:border-indigo-800 dark:bg-slate-900/70 dark:text-indigo-300"
              >
                <CircularProgress size="sm" aria-label="جاري التحميل" />
                <span>جاري تحميل الشركات</span>
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ReportHeader;
